import { useEffect, useState } from 'react';
import * as SingleSelect from '@givebutter/primitives/single-select';
import * as Form from 'components/form';
import { AssignedToFormField } from 'components/tasks/form/AssignedToFormField';
import { DueDateFormField } from 'components/tasks/form/DueDateFormField';
import { RemindAtFormField } from 'components/tasks/form/RemindAtFormField';
import { TaskDescriptionEditor } from 'components/tasks/form/TaskDescriptionEditor';
import { TaskFormValues, taskStatuses } from 'components/tasks/utils/useTaskForm';
import { Editor } from 'tinymce';

interface TaskFormProps {
    inModal?: boolean;
}

const TaskForm = ({ inModal = false }: TaskFormProps) => {
    const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
    const { control, path, watch, setValue } = Form.useGivebutterFormContext<TaskFormValues>();
    const [assigned_to, status] = watch(['assigned_to', 'status']);

    useEffect(() => {
        if (!assigned_to) setValue('remind_at', null);
    }, [assigned_to, setValue]);

    return (
        <div className="flex w-full flex-col gap-4">
            <Form.Container key={status}>
                <Form.Label name={path('status')} className="sr-only">
                    Status
                </Form.Label>
                <Form.SingleSelect control={control} name="status">
                    <SingleSelect.Trigger
                        aria-label="Status"
                        size="sm"
                        className="basis-min-content w-auto self-start px-2 py-0 font-medium ring-0"
                    />
                    <SingleSelect.Content>
                        {taskStatuses.map(({ id, label }) => (
                            <SingleSelect.Item value={id} key={id}>
                                <SingleSelect.ItemText>{label}</SingleSelect.ItemText>
                            </SingleSelect.Item>
                        ))}
                    </SingleSelect.Content>
                </Form.SingleSelect>
            </Form.Container>
            <div className="flex flex-col gap-1">
                <Form.Container>
                    <Form.Label name={path('title')} className="sr-only">
                        Title
                    </Form.Label>

                    <Form.UseFormTextArea
                        control={control}
                        name="title"
                        rows={1}
                        autoResize
                        autoFocus={inModal}
                        placeholder="Task title"
                        className="rounded-none p-0 font-medium ring-0 placeholder:text-gray-500 focus:ring-0"
                        rules={{ required: 'Title is required' }}
                    />
                    <Form.Error name={path('title')} />
                </Form.Container>
                <Form.Container>
                    <Form.Label name={path('description')} className="sr-only">
                        Description
                    </Form.Label>
                    <TaskDescriptionEditor
                        editorInstance={editorInstance}
                        setEditorInstance={setEditorInstance}
                        inModal={inModal}
                    />
                </Form.Container>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <AssignedToFormField />
                <DueDateFormField />
                <RemindAtFormField />
            </div>
        </div>
    );
};

export { TaskForm };
